/**
 * The one fetch layer for the internal staff portal (CLAUDE.md's frontend-engineer
 * brief: "one API client layer, one place where auth headers, refresh, and error
 * normalization live"). Components and route modules call the typed functions in
 * `lib/auth.ts`, never `fetch` directly.
 *
 * Base URL: `import.meta.env.VITE_API_BASE_URL`, never hardcoded outside this one
 * fallback. The fallback matches `internal-api`'s documented default dev port
 * (`api/src/platform/config/env.ts`'s `INTERNAL_API_PORT` default, 4000) so `npm run dev`
 * works out of the box against a locally-running API with no `.env` file at all — see
 * this project's handback for the exact `.env.example` line suggested (this agent must
 * not create or edit any `.env*` file itself, per CLAUDE.md §4).
 */

const API_BASE_URL = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1').replace(
  /\/$/,
  '',
);

/**
 * Exported for the one legitimate non-`fetch` use: `GET /attachments/{id}/download`
 * (API_CONTRACT.md §10) answers with an `X-Accel-Redirect`, not a JSON envelope, so it is
 * navigated to directly (`<a href>`) rather than called through `apiRequest` — the browser
 * sends the session cookie on that navigation the same way it would on any same-origin
 * link, and the JSON envelope machinery has nothing to unwrap here.
 */
export { API_BASE_URL };

/** API_CONTRACT.md §6 — double-submit CSRF's exact cookie/header names. */
const CSRF_COOKIE_NAME = '__Host-ms_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface ApiFieldError {
  field: string;
  code: string;
  params: Record<string, unknown>;
}

export interface ApiErrorBody {
  code: string;
  /** English, developer-facing (API_CONTRACT.md §3.2). NEVER render this to a user. */
  message: string;
  fieldErrors: ApiFieldError[];
  details: Record<string, unknown> | null;
  retryable: boolean;
}

/**
 * `meta.page` carries a different shape per pagination mechanism (API_CONTRACT.md §9.1:
 * `{page, pageSize, totalItems, totalPages}` for offset endpoints, `{nextCursor, hasMore}`
 * for cursor endpoints) — widened to `Record<string, unknown>` here rather than typed as a
 * union, because this file has no per-endpoint knowledge; `lib/files.ts`/`lib/workflow.ts`
 * narrow it at the call site where the endpoint (and therefore the shape) is known.
 */
interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
  meta: (Record<string, unknown> & { correlationId?: string }) | null;
  message: { code: string; params?: Record<string, unknown> } | null;
  error: ApiErrorBody | null;
}

/**
 * One error type for every failure the app renders from. `code` is what the UI branches
 * on (never `message` — API_CONTRACT.md §3.2/§4.3: an unrecognised code falls back to
 * `status`'s class, and `message` is never shown to a user because it is never
 * translated).
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fieldErrors: ApiFieldError[];
  readonly details: Record<string, unknown> | null;
  readonly retryable: boolean;
  /**
   * SCREEN_SPECS.md I24's 500 screen: "includes the request's correlation ID in small
   * print for support purposes ... without exposing anything sensitive." `null` when the
   * server's envelope carried none (e.g. a proxy-level failure with no envelope at all) —
   * that case is itself handled as `NetworkError`, never as an `ApiError` with a fabricated ID.
   */
  readonly correlationId: string | null;

  constructor(body: ApiErrorBody, status: number, correlationId: string | null = null) {
    super(`ApiError ${body.code} (HTTP ${status}): ${body.message}`);
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.fieldErrors = body.fieldErrors;
    this.details = body.details;
    this.retryable = body.retryable;
    this.correlationId = correlationId;
  }
}

/** `fetch` itself failed (offline, DNS, CORS, a proxy that returned non-JSON) — distinct from a server-returned error envelope. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Extra headers, e.g. `Idempotency-Key` (API_CONTRACT.md §9.2 — required on
   * `POST /files/{id}/movements`, among others). Applied after the CSRF header so a
   * caller cannot accidentally shadow it, and before nothing else — there is no other
   * header this layer sets that a caller should override.
   */
  headers?: Record<string, string>;
}

async function rawRequest<T>(path: string, options: RequestOptions): Promise<{ status: number; envelope: ApiEnvelope<T> }> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';

  // API_CONTRACT.md §6: the CSRF token is echoed from the readable cookie on every
  // state-changing request. Absent on the very first request from a cold client — see
  // `apiRequest`'s retry below.
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE_NAME);
    if (token) headers[CSRF_HEADER_NAME] = token;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include', // the session + CSRF cookies are same-origin-through-nginx (ADR-0005); never sent cross-origin
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch (cause) {
    // A response that isn't the documented envelope at all (e.g. a proxy's own HTML
    // error page) is a network-class failure from the caller's point of view, not a
    // contract violation worth surfacing as a typed ApiError.
    throw new NetworkError(cause);
  }

  return { status: response.status, envelope };
}

/**
 * The one fetch entry point. Everything else in this file is a typed convenience over
 * this function.
 *
 * CSRF first-request retry (API_CONTRACT.md §6, `api/src/platform/http/csrf.ts`'s own
 * documented "CONTRACT GAP" note): a cold client's very first state-changing request has
 * no `__Host-ms_csrf` cookie, so the server refuses it once with `CSRF_FAILED` — and that
 * SAME refusal is the response that sets the cookie. Rather than make every call site
 * remember to retry, this function does it centrally, exactly once, so a second
 * `CSRF_FAILED` (a genuinely broken client, not the cold-start case) is not retried
 * forever.
 */
async function requestEnvelope<T>(path: string, options: RequestOptions): Promise<{ status: number; envelope: ApiEnvelope<T> }> {
  let { status, envelope } = await rawRequest<T>(path, options);

  if (!envelope.success && envelope.error?.code === 'CSRF_FAILED') {
    ({ status, envelope } = await rawRequest<T>(path, options));
  }

  if (!envelope.success || envelope.error) {
    throw new ApiError(
      envelope.error ?? {
        code: 'INTERNAL_ERROR',
        message: 'Response envelope had success: false with no error body.',
        fieldErrors: [],
        details: null,
        retryable: true,
      },
      status,
      envelope.meta?.correlationId ?? null,
    );
  }

  return { status, envelope };
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { envelope } = await requestEnvelope<T>(path, options);
  return envelope.data as T;
}

/**
 * Same contract as `apiRequest`, but also returns `meta` — needed by every paginated
 * list (`GET /files`, `GET /files/{id}/movements`, `GET /me/tasks`), since `meta.page` is
 * the only place `{page, pageSize, totalItems, totalPages}` / `{nextCursor, hasMore}`
 * lives (API_CONTRACT.md §9.1). `apiRequest` stays data-only for every caller that has no
 * use for it, which is most of them.
 */
export async function apiRequestMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: ApiEnvelope<T>['meta'] }> {
  const { envelope } = await requestEnvelope<T>(path, options);
  return { data: envelope.data as T, meta: envelope.meta };
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, { method: 'GET', signal });
}

export function apiGetMeta<T>(path: string, signal?: AbortSignal): Promise<{ data: T; meta: ApiEnvelope<T>['meta'] }> {
  return apiRequestMeta<T>(path, { method: 'GET', signal });
}

export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body, signal });
}

export function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, { method: 'PATCH', body, signal });
}

export function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, { method: 'PUT', body, signal });
}

/**
 * `DELETE /users/{id}/role-assignments/{assignmentId}`, `DELETE /sessions/{id}` and
 * `DELETE /me/sessions/{id}` (API_CONTRACT.md §13.10) are the only `DELETE`s this app
 * calls — added for the administration surface rather than left for each call site to
 * hand-roll `apiRequest` directly.
 */
export function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE', signal });
}

/**
 * `apiPost` with room for extra headers — specifically `Idempotency-Key`
 * (API_CONTRACT.md §9.2), required on `POST /files/{id}/movements` and a handful of
 * other writes. Kept separate from `apiPost` so the common case (no header) stays a
 * three-argument call at every existing call site.
 */
export function apiPostWithHeaders<T>(
  path: string,
  body: unknown,
  headers: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body, headers, signal });
}

/**
 * `POST /files/{id}/attachments` is the one call site that cannot go through
 * `apiRequest`: the server reads a raw `multipart/form-data` body (`platform/http/
 * multipart.ts`'s single-file reader), not the JSON envelope every other write sends, so
 * this function must NOT set `Content-Type: application/json` and must NOT
 * `JSON.stringify` the body. It still goes through the same CSRF-cookie-echo and
 * cold-start retry rule as every other state-changing request (API_CONTRACT.md §6) —
 * duplicated here rather than factored through `rawRequest`, because that function's one
 * job is "serialize this body as JSON", which is exactly the thing this call must not do.
 */
async function rawMultipartRequest<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal,
): Promise<{ status: number; envelope: ApiEnvelope<T> }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = readCookie(CSRF_COOKIE_NAME);
  if (token) headers[CSRF_HEADER_NAME] = token;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      // Deliberately NOT setting Content-Type: the browser derives
      // `multipart/form-data; boundary=...` from the FormData instance itself, and
      // setting it manually would drop the boundary the server's reader requires.
      body: formData,
      signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch (cause) {
    throw new NetworkError(cause);
  }

  return { status: response.status, envelope };
}

/**
 * `GET /attachments/{id}/download` is the other call site (besides multipart upload) that
 * cannot go through `apiRequest`: a success response is raw bytes, not a JSON envelope
 * (§10/§13.4) — this app navigates to it directly with `<a href>` for the common
 * "download" case (`FileDetailPage`'s own comment on `API_BASE_URL`'s export), but the new
 * inline-preview feature needs the bytes IN the page (as a `Blob`) rather than as a
 * browser-initiated navigation, so it can render a PDF/image without forcing a save-to-disk
 * dialog. This is that fetch: same-origin cookies, same JSON-envelope error handling as
 * every other failure path, but a `Blob` on success instead of parsed JSON.
 *
 * No CSRF header: this is a `GET`, a safe method, exactly like `apiGet`.
 */
export async function apiGetBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    // A refused download (403/404/etc.) answers the SAME JSON envelope every other
    // endpoint does (`sendOutcomeFailure`) — parse it exactly like `requestEnvelope` does,
    // rather than treating every non-2xx as an opaque network failure.
    let envelope: ApiEnvelope<unknown> | null = null;
    try {
      envelope = (await response.json()) as ApiEnvelope<unknown>;
    } catch {
      envelope = null;
    }
    throw new ApiError(
      envelope?.error ?? {
        code: 'INTERNAL_ERROR',
        message: `HTTP ${response.status} with no error envelope.`,
        fieldErrors: [],
        details: null,
        retryable: response.status >= 500,
      },
      response.status,
      envelope?.meta?.correlationId ?? null,
    );
  }

  try {
    return await response.blob();
  } catch (cause) {
    throw new NetworkError(cause);
  }
}

export async function apiPostMultipart<T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> {
  let { status, envelope } = await rawMultipartRequest<T>(path, formData, signal);

  if (!envelope.success && envelope.error?.code === 'CSRF_FAILED') {
    ({ status, envelope } = await rawMultipartRequest<T>(path, formData, signal));
  }

  if (!envelope.success || envelope.error) {
    throw new ApiError(
      envelope.error ?? {
        code: 'INTERNAL_ERROR',
        message: 'Response envelope had success: false with no error body.',
        fieldErrors: [],
        details: null,
        retryable: true,
      },
      status,
      envelope.meta?.correlationId ?? null,
    );
  }

  return envelope.data as T;
}
